import { useMemo } from 'react';
import type {
    FindingPlanumDocType,
    FindingProfileDocType,
    MediaDocType,
    MediaLinkDocType,
    MediaOwnerType
} from '@ast-data/shared';
import { useDocs } from './useOwnerMedia';
import { chosenPreview } from './previewChoice';

/** Matches no document: a query a hook must run whether it is needed or not. */
const NOTHING = '\u0000';

export interface PreviewScope {
    /** The pictures starred anywhere in the scope. */
    starred: MediaDocType[];
    /** The one of them the field list card shows. */
    previewId: string | undefined;
}

/**
 * The pictures that share ONE star: the description's own — and for a Befund,
 * a Planum or a Profil, those of the whole Befund, since only the Befund has a
 * card on the field list. A star set anywhere in it clears the others, so
 * taking it back later brings no forgotten choice back.
 *
 * The media and link queries are per PROJECT and filtered here: a selector
 * naming the scope's owners would change once the Befund is known, and the
 * live query answers [] in between — the star blinked away. The starred
 * pictures of a project are few anyway.
 */
export function usePreviewScope(
    projectId: string,
    ownerType: MediaOwnerType,
    ownerId: string
): PreviewScope {
    const planumHere = useDocs<FindingPlanumDocType>('finding_planums', {
        id: ownerType === 'findingPlanum' ? ownerId : NOTHING
    });
    const profileHere = useDocs<FindingProfileDocType>('finding_profiles', {
        id: ownerType === 'findingProfile' ? ownerId : NOTHING
    });
    const findingId =
        ownerType === 'finding'
            ? ownerId
            : (planumHere[0]?.findingId ?? profileHere[0]?.findingId ?? '');

    const planums = useDocs<FindingPlanumDocType>('finding_planums', {
        findingId: findingId || NOTHING
    });
    const profiles = useDocs<FindingProfileDocType>('finding_profiles', {
        findingId: findingId || NOTHING
    });
    const ownerIds = useMemo(
        () =>
            findingId
                ? [
                      findingId,
                      ...planums.map((planum) => planum.id),
                      ...profiles.map((profile) => profile.id)
                  ]
                : [ownerId],
        [findingId, planums, profiles, ownerId]
    );

    const starredInProject = useDocs<MediaDocType>('media', {
        projectId,
        previewChosenAt: { $ne: '' }
    });
    const projectLinks = useDocs<MediaLinkDocType>('media_links', {
        projectId
    });
    // A picture linked in from another Teilmaßnahme of the family.
    const linkedIds = useMemo(() => {
        const owners = new Set(ownerIds);
        return projectLinks
            .filter((link) => owners.has(link.ownerId))
            .map((link) => link.mediaId);
    }, [projectLinks, ownerIds]);
    const linkedMedia = useDocs<MediaDocType>('media', {
        id: { $in: linkedIds }
    });

    return useMemo(() => {
        const owners = new Set(ownerIds);
        const byId = new Map(
            [
                ...starredInProject.filter((media) =>
                    owners.has(media.ownerId)
                ),
                ...linkedMedia
            ].map((media) => [media.id, media])
        );
        const starred = [...byId.values()].filter(
            (media) => media.previewChosenAt
        );
        return { starred, previewId: chosenPreview(starred)?.id };
    }, [starredInProject, linkedMedia, ownerIds]);
}
